/**
 * Deduces the face of a speaker nobody has mapped from the face left over
 * while they talk (PLAT-12143 experiment): rule out the faces on screen that
 * belong to other known speakers, and if exactly one remains, that's a vote
 * for it. Fits wide shots and panels, where everyone is on screen all the time
 * and nobody is ever alone.
 *
 * A face is assigned only on consistent evidence, and only to one speaker: if
 * two unmapped voices both point at it (say an off-camera moderator and the
 * panelist), it goes to the one with clearly more votes, or to neither.
 */

import { extractSpeakerIntervals } from './speakerIntervalUtils';

import type { TranscriptWord } from './speakerIntervalUtils';
import type { FaceDetection } from './types';

/** Fewest leftover-face moments before a face is assigned. */
export const MIN_ELIMINATION_VOTES = 5;
/** The winner needs this many times the runner-up's votes, both per speaker and per face. */
export const ELIMINATION_DOMINANCE = 3;
/** Faces smaller than this share of the frame's largest face are ignored. */
const MIN_FACE_AREA_RATIO = 0.25;

export interface EliminationDeduction {
  /** face_label → speaker_label, for unmapped speakers with clear evidence. */
  faceToSpeaker: Record<string, string>;
  /** speaker_label → face_label → frames where it was the only unclaimed face. */
  votes: Record<string, Record<string, number>>;
  /** face_label → media time of its first leftover moment, for spot checks. */
  firstTime: Record<string, number>;
  /** speaker_label → face_label → media times of every vote, for spot checks. */
  voteTimes: Record<string, Record<string, number[]>>;
}

export function deduceFacesByElimination(
  transcript: ReadonlyArray<TranscriptWord>,
  detections: ReadonlyArray<Pick<FaceDetection, 'time' | 'label' | 'box'>>,
  speakerMap: Readonly<Record<string, readonly string[]>>,
): EliminationDeduction {
  const claimedBy = new Map<string, string>();
  for (const [speakerLabel, faceLabels] of Object.entries(speakerMap)) {
    for (const faceLabel of faceLabels) claimedBy.set(faceLabel, speakerLabel);
  }
  const mappedSpeakers = new Set(
    Object.entries(speakerMap)
      .filter(([, faceLabels]) => faceLabels.length > 0)
      .map(([speakerLabel]) => speakerLabel),
  );

  const intervals = extractSpeakerIntervals(transcript)
    .filter(interval => interval.speakerLabel !== 'unknown')
    .sort((a, b) => a.startTime - b.startTime);
  const sorted = [...detections].sort((a, b) => a.time - b.time);

  const votes: Record<string, Record<string, number>> = {};
  const firstTime: Record<string, number> = {};
  const voteTimes: Record<string, Record<string, number[]>> = {};
  let intervalIndex = 0;

  for (let start = 0; start < sorted.length; ) {
    const time = sorted[start].time;
    let end = start;
    while (end < sorted.length && sorted[end].time === time) end++;
    const frame = sorted.slice(start, end);
    start = end;

    while (
      intervalIndex < intervals.length &&
      intervals[intervalIndex].endTime < time
    ) {
      intervalIndex++;
    }
    const interval = intervals[intervalIndex];
    if (!interval || interval.startTime > time) continue;
    const speaker = interval.speakerLabel;
    if (mappedSpeakers.has(speaker)) continue;

    const largest = Math.max(...frame.map(({ box }) => box[2] * box[3]));
    const unclaimed = new Set(
      frame
        .filter(({ box }) => box[2] * box[3] >= largest * MIN_FACE_AREA_RATIO)
        .map(({ label }) => label)
        .filter(label => !claimedBy.has(label)),
    );
    if (unclaimed.size !== 1) continue;

    const [faceLabel] = unclaimed;
    firstTime[faceLabel] ??= time;
    const speakerVotes = (votes[speaker] ??= {});
    speakerVotes[faceLabel] = (speakerVotes[faceLabel] ?? 0) + 1;
    ((voteTimes[speaker] ??= {})[faceLabel] ??= []).push(time);
  }

  // Each speaker's best face, if it clearly dominates their other candidates.
  const candidates: Array<{ speaker: string; face: string; count: number }> =
    [];
  for (const [speaker, byFace] of Object.entries(votes)) {
    const [top, runnerUp] = Object.entries(byFace).sort((a, b) => b[1] - a[1]);
    if (
      top[1] >= MIN_ELIMINATION_VOTES &&
      top[1] >= (runnerUp?.[1] ?? 0) * ELIMINATION_DOMINANCE
    ) {
      candidates.push({ speaker, face: top[0], count: top[1] });
    }
  }

  // One speaker per face: the clear winner, or nobody.
  const faceToSpeaker: Record<string, string> = {};
  for (const face of new Set(candidates.map(c => c.face))) {
    const [winner, runnerUp] = candidates
      .filter(c => c.face === face)
      .sort((a, b) => b.count - a.count);
    if (!runnerUp || winner.count >= runnerUp.count * ELIMINATION_DOMINANCE) {
      faceToSpeaker[face] = winner.speaker;
    }
  }

  return { faceToSpeaker, votes, firstTime, voteTimes };
}
